import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuthContext } from '../context/AuthContext';
import { CaseInputType, useCaseIntake } from '../context/CaseIntakeContext';
import { completeRegistration } from '../queries/completeregistration';
import { usePersistentNavigate } from '../utils/navigation';
import PersistentLink from './PersistentLink';

const RegistrationContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#f8fafc',
  padding: '16px',
});

const RegistrationCard = styled(Paper)({
  padding: '40px',
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: '8px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
});

// How did you hear about us options
const referralSources = [
  "From a colleague",
  "Google",
  "Email",
  "Social media",
  "Conference",
  "Other"
];

const CompleteRegistration: React.FC = () => {
  const navigate = usePersistentNavigate();
  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [location, setLocation] = useState('');
  const [npi, setNpi] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [referringColleague, setReferringColleague] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const { accessToken, setCompletedRegistration, setAccessToken, logout } = useAuthContext();
  const { caseContextJson, caseContextText, caseInputType } = useCaseIntake();

  // NPI validation
  const [npiError, setNpiError] = useState(false);
  const [npiErrorMessage, setNpiErrorMessage] = useState('');

  useEffect(() => {
    if (npi === '') {
      setNpiError(false);
      setNpiErrorMessage('');
    } else if (!/^\d+$/.test(npi)) {
      setNpiError(true);
      setNpiErrorMessage('NPI must contain only numerical digits');
    } else if (npi.length !== 10) {
      setNpiError(true);
      setNpiErrorMessage('NPI must be exactly 10 digits');
    } else {
      setNpiError(false);
      setNpiErrorMessage('');
    }
  }, [npi]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!name || !institution || !location || !npi || !agreedToTerms) {
      return; // Form validation will handle displaying errors
    }

    // Check NPI validation
    if (npiError) {
      return;
    }

    // Add your API call to complete registration here
    console.log({
      name,
      institution,
      location,
      npi,
      referralSource,
      referringColleague: referralSource === 'From a colleague' ? referringColleague : '',
      agreedToTerms
    });

    await completeRegistration({
      npi: npi || '',
      name,
      referralType: referralSource,
      referralName: referralSource === 'From a colleague' ? referringColleague : '',
      institution,
      location,
      acceptedTermsOfService: agreedToTerms
    }, accessToken || '', setAccessToken, logout);
    setCompletedRegistration(true);

    if (caseContextText || caseContextJson || caseInputType == CaseInputType.FILE) {
      navigate('/case-report');
      return;
    }

    // Navigate to home or wherever appropriate after registration
    navigate('/');
  };

  useEffect(() => {
    if (!accessToken) {
      navigate('/login');
    }
  }, []);

  return (
    <RegistrationContainer maxWidth={false}>
      <RegistrationCard elevation={0}>
        {/* PrecisCa AI Logo/Title as a link back to landing page */}
        <PersistentLink
          to="/"
          style={{ textDecoration: 'none' }}
        >
          <Box
            component="img"
            src="/ai logo no background.png"
            alt="Precisca AI"
            sx={{
              height: { xs: 48, sm: 64 },
              width: 'auto',
              display: 'block',
              mb: 2,
              cursor: 'pointer'
            }}
          />
        </PersistentLink>

        <Typography
          variant="body1"
          sx={{
            mb: 3,
            mt: 3,
            color: '#4a5568',
            textAlign: 'center'
          }}
        >
          Complete your registration.
        </Typography>

        {/* Form */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ width: '100%' }}
        >
          {/* Name Field */}
          <TextField
            fullWidth
            id="name"
            label="Name"
            placeholder="Firstname Lastname"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            InputLabelProps={{ required: false }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4299e1',
                },
              },
            }}
          />

          {/* Institution/Practice Field */}
          <TextField
            fullWidth
            id="institution"
            label="Institution or Practice"
            variant="outlined"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            required
            InputLabelProps={{ required: false }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4299e1',
                },
              },
            }}
          />

          {/* Institution Location Field */}
          <TextField
            fullWidth
            id="location"
            label="Institution Location"
            placeholder="City, State/Province, Country"
            variant="outlined"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
            InputLabelProps={{ required: false }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4299e1',
                },
              },
            }}
          />

          {/* NPI Field */}
          <TextField
            fullWidth
            id="npi"
            label="NPI"
            variant="outlined"
            value={npi}
            onChange={(e) => setNpi(e.target.value)}
            required
            error={npiError}
            InputLabelProps={{ required: false }}
            sx={{
              mb: npiError ? 0.5 : 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: npiError ? '#f56565' : '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: npiError ? '#f56565' : '#cbd5e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: npiError ? '#f56565' : '#4299e1',
                },
              },
            }}
          />
          {npiError && (
            <FormHelperText error sx={{ ml: 1.5, mt: -0.5, mb: 0.5 }}>
              {npiErrorMessage}
            </FormHelperText>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: '#718096', mb: 2, mt: 0.5, ml: 1.5 }}>
            PrecisCa AI is currently only available for registered health care professionals.
          </Typography>

          {/* How did you hear about us dropdown */}
          <TextField
            select
            fullWidth
            id="referralSource"
            label="How did you hear about us?"
            value={referralSource}
            onChange={(e) => setReferralSource(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4299e1',
                },
              },
            }}
          >
            <MenuItem value="" disabled>
              <em>Select an option</em>
            </MenuItem>
            {referralSources.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>

          {/* Conditional field for colleague name */}
          {referralSource === 'From a colleague' && (
            <TextField
              fullWidth
              id="referringColleague"
              label="Name of referring colleague"
              variant="outlined"
              value={referringColleague}
              onChange={(e) => setReferringColleague(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#4299e1',
                  },
                },
              }}
            />
          )}

          {/* Terms and conditions checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                sx={{
                  color: '#a0aec0',
                  '&.Mui-checked': { color: '#4299e1' },
                }}
              />
            }
            sx={{
              mt: 1.5,
              mb: 1,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                color: '#4a5568',
                fontSize: '0.9rem',
                mt: 0.5
              }
            }}
            label="I am a licensed healthcare professional and all information provided above is accurate."
          />

          {/* Complete Registration Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!name || !institution || !location || !npi || !agreedToTerms || npiError}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              bgcolor: '#4299e1',
              '&:hover': {
                bgcolor: '#2c5282',
              },
              '&.Mui-disabled': {
                bgcolor: '#cbd5e0',
              },
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: 'none',
              borderRadius: '4px',
            }}
          >
            Complete Registration
          </Button>
        </Box>
      </RegistrationCard>
    </RegistrationContainer>
  );
};

export default CompleteRegistration;